import { Controller } from "@hotwired/stimulus"
import { paidIds, PAID_EVENT } from "../helpers/due_today_paid"
import { moneyText } from "../helpers/money_text"

// The dashboard's Due today card.
//
// The server renders every expense due today; "Paid" is device-local, so this
// hides the ones marked paid on this phone today and recounts what is left.
// With nothing left the whole section folds away, gap included.
//
// Two markups share it:
//   * budgets/_due_today — the review strip. A hidden stash holds a tile per
//     expense; the fan, count, summary and total are rebuilt from the stash
//     entries that are still unpaid.
//   * budgets/_due_today.html+web — the web shell's stack: one tile and name
//     per expense, all hidden but the front one, plus "+N more" and the total.
//
// Tapping the card opens the deck (due_deck_controller), which holds what is
// due across every budget.
export default class extends Controller {
  static targets = ["stash", "fan", "count", "summary", "total", "tile", "name", "more"]
  static values = { today: String, money: Object, moreTemplate: String }

  // The strip fans this many (budgets/_review_strip).
  static FAN = 3

  connect() {
    this._onPaid = () => this.refresh()
    window.addEventListener(PAID_EVENT, this._onPaid)
    this.refresh()
  }

  disconnect() {
    window.removeEventListener(PAID_EVENT, this._onPaid)
  }

  open() {
    this.dispatch("open", { prefix: "due-deck" })
  }

  refresh() {
    const paid = paidIds(this.todayValue)
    if (this.hasStashTarget) this._refreshStrip(paid)
    else if (this.hasTileTarget) this._refreshStack(paid)
  }

  // The strip: rebuilt only once something is paid — the server's render
  // already shows the full set.
  _refreshStrip(paid) {
    const all = Array.from(this.stashTarget.children)
    const left = all.filter((item) => !paid.has(item.dataset.id))

    if (left.length === 0) {
      this.element.classList.add("hidden")
      return
    }
    if (left.length === all.length) return

    const front = left.slice(0, this.constructor.FAN)
    this.fanTarget.replaceChildren(...front.map((item) => item.firstElementChild.cloneNode(true)))
    this.fanTarget.dataset.count = front.length

    if (this.hasCountTarget) this.countTarget.textContent = left.length
    if (this.hasSummaryTarget) {
      const more = left.length > 1 ? ` ${this.moreTemplateValue.replace("__N__", left.length - 1)}` : ""
      this.summaryTarget.textContent = `${left[0].dataset.name}${more}`
    }
    this._total(left)
  }

  // The web stack: the first remaining expense fronts the card, "+N more" and
  // the total cover the rest.
  _refreshStack(paid) {
    const left = this.tileTargets.filter((tile) => !paid.has(tile.dataset.id))

    if (left.length === 0) {
      this.element.classList.add("hidden")
      return
    }

    const frontId = left[0].dataset.id
    this.tileTargets.forEach((tile) => tile.classList.toggle("hidden", tile.dataset.id !== frontId))
    this.nameTargets.forEach((name) => name.classList.toggle("hidden", name.dataset.id !== frontId))

    if (this.hasCountTarget) this.countTarget.textContent = left.length
    if (this.hasMoreTarget) {
      this.moreTarget.textContent = left.length > 1 ? this.moreTemplateValue.replace("__N__", left.length - 1) : ""
    }
    this._total(left)
  }

  _total(items) {
    if (!this.hasTotalTarget) return

    const total = items.reduce((sum, item) => sum + Number(item.dataset.amount), 0)
    this.totalTarget.textContent = moneyText(total, this.moneyValue, "−")
  }
}
